import _ from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  COLLABORATION_PROVIDERS,
  DAYS_PARAM,
  LOCAL_STORAGE_KEYS
} from 'common/constants/constants';
import {
  ACCOUNT_TYPES_MAP,
  FEATURE_FLAGS_NAMES,
  PERMISSION_SCOPE_TYPE
} from '@linearb/linjs-common';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { getParsedToken } from 'common/services/token.service';
import { isAdmin, isSuperUser } from 'common/services/accounts.service';
import { settingsSelector } from './settings';
import { createGetSettingsMethods } from 'common/services/settings.service.utils';

const getInvitations = state => state.invitations;
const localStorageAccessor = BrowserStorageAccessor().local;
export const accountsSelector = ({ accounts }) => accounts || [];

export const myAccountSelector = createSelector(accountsSelector, accounts => {
  let myAccountId = localStorageAccessor.get(LOCAL_STORAGE_KEYS.ACCOUNT_ID);
  let account = accounts.filter(Boolean).find(a => a.id === myAccountId * 1);

  if (!account) {
    // can be if saved localstorage key is from another account (switch orgs or support account)
    myAccountId = getParsedToken()?.id;
    if (myAccountId) {
      localStorageAccessor.set(LOCAL_STORAGE_KEYS.ACCOUNT_ID, myAccountId);
    }
    account =
      accounts.filter(Boolean).find(a => a.id === myAccountId * 1) || {};
  }

  return account;
});

export const accountsByIdSelector = createSelector(
  [accountsSelector],
  accounts => _.keyBy(accounts, 'id')
);

export const getAccountsForUsersTableWithPendingInvitations = createSelector(
  [accountsSelector, getInvitations],
  (accounts, invitations) => {
    const accountsWithoutSuper = accounts.filter(
      ({ type_id }) => type_id !== ACCOUNT_TYPES_MAP.SuperUser
    );

    const emailInvitations = invitations.filter(
      ({ sensor_id }) => sensor_id !== COLLABORATION_PROVIDERS.SLACK
    );
    const pendingInvitations = _.differenceBy(
      emailInvitations,
      accountsWithoutSuper,
      'email'
    );
    const nonPendingInvitations = _.differenceBy(
      emailInvitations,
      pendingInvitations,
      'email'
    );
    const nonPendingInvitationsByEmail = _.keyBy(
      nonPendingInvitations,
      'email'
    );

    return _.orderBy(
      [
        ...accountsWithoutSuper.map(account => ({
          ...nonPendingInvitationsByEmail[account.email],
          ...account
        })),
        ...pendingInvitations
          .filter(i => !i.confirmed_at)
          .map(invitation => ({
            ...invitation,
            invitationId: invitation.id,
            type_id:
              _.get(invitation, 'account_type_id') || ACCOUNT_TYPES_MAP.Viewer,
            isPending: true,
            isExpired: moment(invitation.invited_at).isAfter(
              moment().add(7, DAYS_PARAM)
            )
          }))
      ],
      'type_id'
    );
  }
);

export const isMultiOrgSelector = createSelector(myAccountSelector, account =>
  _.get(account, 'is_multi_org', false)
);

export const isAdminSelector = createSelector(myAccountSelector, account =>
  isAdmin(account)
);

export const isSuperUserSelector = createSelector(myAccountSelector, account =>
  isSuperUser(account)
);

export function hasAllTeamsScopeSelector(state) {
  return createSelector(
    [myAccountSelector, settingsSelector],
    ({ account_scopes = [] }, settings) => {
      const { getSettingOrDefinitionValue } =
        createGetSettingsMethods(settings);
      const isRBACEnabled = getSettingOrDefinitionValue({
        name: FEATURE_FLAGS_NAMES.isRBACEnabled
      });
      return isRBACEnabled
        ? account_scopes.some(
            ({ scope_type }) => scope_type === PERMISSION_SCOPE_TYPE.ALL_TEAMS
          )
        : true;
    }
  )(state);
}
